// Constants
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { DeltaType } from "@ms/planner-models/lib/local/client/deltaSync/Enumerations";
// Tables
import { EntityTable, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
// Slices
import { AbstractSlice } from "./AbstractSlice";
import isEmpty from "lodash/isEmpty";
import { AbsentStoreEntityStatus, getStateBasedOnResponse, updateStatus } from "../utilities/StatusUtilities";
/**
 * Enhanced slice implementation for diffable types T that extends IDiffable<T>.
 * Extends the basic Slice with update and baseline functionality.
 * Uses EntityTable instead of BasicTable for diffing support.
 */ export class UpdatableSlice extends AbstractSlice {
    table() {
        return this.itemTable;
    }
    get(id, readBaseline) {
        const entity = this.table().get(id);
        const status = this.statusTable.get(id) ?? AbsentStoreEntityStatus;
        return {
            data: entity ? readBaseline ? entity.baseline : entity.value : null,
            status
        };
    }
    getCollection(collectionDescriptor, readBaseline) {
        const rowIds = this.table().index(collectionDescriptor.name).partition(collectionDescriptor.value).itemIds();
        const items = [];
        for (const rowId of rowIds){
            const entity = this.table().get(rowId);
            if (entity) {
                items.push(readBaseline ? entity.baseline : entity.value);
            }
        }
        const statusTable = this.collectionStatusTables[collectionDescriptor.name];
        const status = statusTable?.get(collectionDescriptor.value) ?? AbsentStoreEntityStatus;
        return {
            data: items,
            status
        };
    }
    createTable() {
        const table = new EntityTable(this.name);
        for (const indexName of Object.keys(this.indexDefinitions)){
            const indexDefinition = this.indexDefinitions[indexName];
            registerIndex(table, indexName, indexDefinition);
        }
        return table;
    }
    handleInput(id, item) {
        let updated = false;
        const currentEntity = this.table().get(id);
        if (!currentEntity) {
            this.table().add(id, item);
            updated = true;
        } else if (this.isNewer(currentEntity.baseline, item)) {
            currentEntity.updateBaseline(item);
            updated = true;
        }
        return updated;
    }
    handleUpdateStarted(id, updateKey, delta, promotedPropertyOptimisticCalculator) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        if (sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is soft deleted, we cannot update it.
            return false;
        }
        if (!delta) {
            return false;
        }
        let realDelta = sliceCopy.value.getDiff(sliceCopy.value.applyDiffs(delta));
        if (isEmpty(realDelta)) {
            return false;
        }
        if (promotedPropertyOptimisticCalculator) {
            const promotedDelta = promotedPropertyOptimisticCalculator(sliceCopy.value, realDelta);
            realDelta = {
                ...realDelta,
                ...promotedDelta
            };
        }
        sliceCopy.pushDelta(realDelta, updateKey);
        return true;
    }
    handleUpdateFailed(id, updateKey, response) {
        let sliceChanged = false;
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is soft deleted or does not exist, we cannot update it.
            return false;
        }
        if (sliceCopy.getDelta(updateKey)) {
            // If there is a delta for this update, we can revert it.
            sliceCopy.revertDelta(updateKey);
            sliceChanged = true;
        }
        // Update status if needed
        if (response) {
            const newState = getStateBasedOnResponse(response);
            if (newState) {
                sliceChanged = updateStatus(this.statusTable, id, {
                    state: newState
                }) || sliceChanged;
            }
        }
        return sliceChanged;
    }
    handleUpdateSucceeded(id, updateKey, updatedItem) {
        let sliceChanged = false;
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is soft deleted or does not exist, we cannot update it.
            return false;
        }
        if (sliceCopy.getDelta(updateKey)) {
            // If there is a delta for this update, we can apply it.
            sliceCopy.commitDelta(updateKey, updatedItem);
            sliceChanged = true;
        }
        return sliceChanged;
    }
    handleDeleteStarted(id) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is soft deleted or does not exist, we cannot delete it.
            return false;
        }
        sliceCopy.softDelete();
        updateStatus(this.statusTable, id, {
            state: StoreEntityState.SoftDeleted
        });
        return true;
    }
    handleDeleteFailed(id, response) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            // If the entity does not exist, we don't need to do anything.
            return false;
        }
        let sliceChanged = false;
        if (sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is already soft deleted, we can revert the soft delete.
            sliceCopy.undelete();
            updateStatus(this.statusTable, id, {
                state: StoreEntityState.Present
            });
            sliceChanged = true;
        }
        // Update status based on response
        if (response) {
            const newState = getStateBasedOnResponse(response);
            if (newState) {
                sliceChanged = updateStatus(this.statusTable, id, {
                    state: newState
                }) || sliceChanged;
            }
        }
        return sliceChanged;
    }
    handleCreateSucceeded(localId, createdItem, serverId) {
        const sliceCopy = this.table().get(localId);
        if (!sliceCopy) {
            return false;
        }
        // Update the baseline with the server version
        sliceCopy.updateBaseline(createdItem);
        // If the created item ID is different from the local ID, we need to re-key the entity
        if (serverId !== localId) {
            this.table().reKey(localId, serverId);
            this.deleteEntityAndStatus(localId);
        }
        updateStatus(this.statusTable, serverId, {
            state: StoreEntityState.Present
        });
        return true;
    }
    /**
     * Helper method to add an entity and its status
     */ upsertEntityAndStatus(id, item) {
        let sliceChanged = false;
        sliceChanged = this.handleInput(id, item) || sliceChanged;
        sliceChanged = updateStatus(this.statusTable, id, {
            state: StoreEntityState.Present
        }) || sliceChanged;
        return sliceChanged;
    }
    /**
     * Handles a single delta sync item based on its type, including Update operations.
     * Extends the base implementation to support Update delta type.
     * @param itemId The ID of the item
     * @param item The delta entity containing the operation type and payload
     * @returns True if the slice was changed, false otherwise
     */ handleDeltaSyncItem(itemId, item) {
        switch(item.type){
            case DeltaType.Update:
                return this.handleDeltaSyncUpdate(itemId, item.entity);
            default:
                return super.handleDeltaSyncItem(itemId, item);
        }
    }
    /**
     * Handles a delta sync update for a specific entity.
     * @param id The ID of the entity to update.
     * @param item The delta entity containing the update information.
     * @returns True if the update was successful, false otherwise.
     */ handleDeltaSyncUpdate(id, item) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            // If the entity does not exist, we cannot update it.
            return false;
        }
        return this.handleInput(id, sliceCopy.baseline.applyDiffs(item));
    }
    constructor(name, indexDefinitions, isNewer, configProvider){
        super(name, indexDefinitions, configProvider), this.isNewer = isNewer, this.itemTable = this.createTable();
    }
}
